import { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs, { Dayjs } from "dayjs";
import useClients from "../client-config/data/use-clients";
import { useNotification } from "../../context/notification-provider";
import {
  getMdaFilterOptions,
  getMdaClaims,
  markMdaClaimsAsRead,
  searchMdaClaimsByReference,
} from "./mda-service";

import MdaEmptyState from "./mda-empty-state";
import MdaClaimsTable from "./mda-claims-table";
import MdaClaimRuleBreaksDrawer from "./mda-claim-rule-breaks-drawer";
import MdaClaimReferenceSearch from "./mda-claim-reference-search";
import MdaFiltersPanel from "./mda-filters-panel";
import MdaModeChooser from "./mda-mode-chooser";
import MdaPageHeader from "./mda-page-header";
import { getClaimRowId } from "./mda-table-helpers";
import {
  ActiveFilter,
  MdaClaim,
  MdaFilterActions,
  MdaFilterOption,
  MdaFilterValues,
  MdaSearchMode,
  MdaSelectOption,
  ReviewStatus,
  RiskRating,
} from "./mda-types";
import {
  getSelectedClaimStatuses,
  getSelectedRiskRatings,
  getSelectedReviewStatus,
  toTitleCaseRiskRating,
  toReviewStatus,
  buildAppliedFilters,
  getFilterItems,
} from "./mda-helpers";
import RequestSpreadsheetDialog from "./request-spreadsheet-dialog";

const Mda = () => {
  const { showNotification } = useNotification();
  const { data: clientOptions = [] } = useClients();
  const [filterOptions, setFilterOptions] = useState<MdaFilterOption[]>([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [client, setClient] = useState("");
  const [riskRatings, setRiskRatings] = useState<RiskRating[]>([]);
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>("");
  const [claimStatuses, setClaimStatuses] = useState<string[]>([]);
  const [incidentDateFrom, setIncidentDateFrom] = useState<Dayjs | null>(null);
  const [incidentDateTo, setIncidentDateTo] = useState<Dayjs | null>(null);
  const [claims, setClaims] = useState<MdaClaim[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [showRequestSpreadsheetDialog, setShowRequestSpreadsheetDialog] =
    useState(false);
  const [mode, setMode] = useState<MdaSearchMode>("choose");
  // The claims to step through in the rule breaks panel, in grid order, and the
  // one currently shown. Null while the panel is closed.
  const [ruleBreaksNavigation, setRuleBreaksNavigation] = useState<{
    claims: MdaClaim[];
    index: number;
  } | null>(null);
  const ruleBreaksClaim =
    ruleBreaksNavigation?.claims[ruleBreaksNavigation.index] ?? null;

  const handleViewRuleBreaks = (claim: MdaClaim, orderedClaims: MdaClaim[]) => {
    const index = orderedClaims.findIndex(
      (item) => getClaimRowId(item) === getClaimRowId(claim),
    );
    setRuleBreaksNavigation(
      index === -1
        ? { claims: [claim], index: 0 }
        : { claims: orderedClaims, index },
    );
  };

  const stepRuleBreaks = (offset: number) =>
    setRuleBreaksNavigation((current) =>
      current ? { ...current, index: current.index + offset } : current,
    );

  const selectedClient = clientOptions.find(({ name }) => name === client);
  const selectedClientId = selectedClient?.id;
  const selectedClaims = claims
    .filter(({ selected }) => !selected)
    .map((claim) => ({ ...claim, selected: !claim.selected }));

  useEffect(() => {
    let isCurrent = true;

    setClaimStatuses([]);
    setRiskRatings([]);
    setReviewStatus("");
    setIncidentDateFrom(null);
    setIncidentDateTo(null);
    setClaims([]);
    setHasApplied(false);
    if (!selectedClientId) {
      setFilterOptions([]);
      setFilterOptionsLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    setFilterOptions([]);
    setFilterOptionsLoading(true);
    getMdaFilterOptions(selectedClientId)
      .then(({ filters }: { filters: MdaFilterOption[] }) => {
        if (isCurrent) {
          const initialFilters = filters.map((filter) => {
            if (!filter.isDropdown || filter.selectionMode !== 2) return filter;
            return {
              ...filter,
              items: filter.items.map((item) => ({ ...item, selected: true })),
            };
          });
          setFilterOptions(initialFilters);
          setClaimStatuses(getSelectedClaimStatuses(initialFilters));
          setRiskRatings(getSelectedRiskRatings(initialFilters));
          setReviewStatus(getSelectedReviewStatus(initialFilters));
          const incidentDate = initialFilters.find(
            ({ key }) => key === "IncidentDate",
          );
          setIncidentDateFrom(
            incidentDate?.range?.from ? dayjs(incidentDate.range.from) : null,
          );
          setIncidentDateTo(
            incidentDate?.range?.to ? dayjs(incidentDate.range.to) : null,
          );
        }
      })
      .catch(() => {
        if (isCurrent) setFilterOptions([]);
      })
      .finally(() => {
        if (isCurrent) setFilterOptionsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [selectedClientId]);

  const claimStatusOptions = useMemo<MdaSelectOption[]>(
    () =>
      getFilterItems(filterOptions, "Status").map((item) => ({
        value: item.value,
        label: item.description,
      })),
    [filterOptions],
  );

  const riskRatingOptions = useMemo<RiskRating[]>(() => {
    const ratings = getFilterItems(filterOptions, "RiskRating")
      .map((item) => toTitleCaseRiskRating(item.description || item.value))
      .filter((rating): rating is RiskRating => Boolean(rating));

    return ratings.length ? ratings : ["High", "Medium", "Low"];
  }, [filterOptions]);

  const reviewStatusOptions = useMemo<MdaSelectOption[]>(() => {
    const statuses = getFilterItems(filterOptions, "ReviewStatus")
      .map<MdaSelectOption | null>((item) => {
        const value = toReviewStatus(item.value || item.description);
        return value ? { value, label: item.description } : null;
      })
      .filter((status): status is MdaSelectOption => Boolean(status));

    return [{ value: "all", label: "All" }, ...statuses];
  }, [filterOptions]);

  const values: MdaFilterValues = {
    client,
    riskRatings,
    reviewStatus,
    claimStatuses,
    incidentDateFrom,
    incidentDateTo,
  };
  const hasAllClaimStatusesSelected =
    Boolean(claimStatusOptions.length) &&
    claimStatusOptions.every((status) => claimStatuses.includes(status.value));
  const renderedFilters = useMemo(
    () =>
      buildAppliedFilters(
        filterOptions,
        riskRatings,
        reviewStatus,
        claimStatuses,
        incidentDateFrom,
        incidentDateTo,
      ),
    [
      filterOptions,
      riskRatings,
      reviewStatus,
      claimStatuses,
      incidentDateFrom,
      incidentDateTo,
    ],
  );
  const canApplyFilters =
    !filterOptionsLoading &&
    (riskRatings.length > 0 ||
      Boolean(reviewStatus) ||
      claimStatuses.length > 0 ||
      Boolean(incidentDateFrom) ||
      Boolean(incidentDateTo) ||
      renderedFilters.some(
        (filter) =>
          filter.items.some((item) => item.selected) ||
          Boolean(filter.range?.from) ||
          Boolean(filter.range?.to),
      ));

  const handleFilterChange = (filter: MdaFilterOption) => {
    setFilterOptions((current) =>
      current.map((option) => (option.key === filter.key ? filter : option)),
    );
    if (filter.key === "Status") {
      setClaimStatuses(
        filter.items.filter((item) => item.selected).map((item) => item.value),
      );
    }
    if (filter.key === "RiskRating")
      setRiskRatings(getSelectedRiskRatings([filter]));
    if (filter.key === "ReviewStatus")
      setReviewStatus(getSelectedReviewStatus([filter]));
    if (filter.key === "IncidentDate") {
      setIncidentDateFrom(filter.range?.from ? dayjs(filter.range.from) : null);
      setIncidentDateTo(filter.range?.to ? dayjs(filter.range.to) : null);
    }
  };

  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const filters: ActiveFilter[] = [];
    if (client) filters.push({ key: "client", label: client });
    riskRatings.forEach((rating) =>
      filters.push({ key: `risk-${rating}`, label: rating }),
    );
    if (reviewStatus === "read") {
      filters.push({ key: "read", label: "Read" });
    }
    if (reviewStatus === "unread") {
      filters.push({ key: "unread", label: "Unread" });
    }
    if (hasAllClaimStatusesSelected) {
      filters.push({ key: "claim-status-all", label: "All claim statuses" });
    } else {
      claimStatuses.forEach((claimStatus) => {
        const statusLabel =
          claimStatusOptions.find((status) => status.value === claimStatus)
            ?.label ?? claimStatus;
        filters.push({
          key: `claim-status-${claimStatus}`,
          label: statusLabel,
        });
      });
    }
    if (incidentDateFrom || incidentDateTo) {
      const fromLabel = incidentDateFrom
        ? incidentDateFrom.format("DD/MM/YYYY")
        : "Any";
      const toLabel = incidentDateTo
        ? incidentDateTo.format("DD/MM/YYYY")
        : "Any";
      filters.push({
        key: "incident-date",
        label: `Incident Date: ${fromLabel} - ${toLabel}`,
      });
    }
    return filters;
  }, [
    claimStatuses,
    claimStatusOptions,
    client,
    hasAllClaimStatusesSelected,
    incidentDateFrom,
    incidentDateTo,
    reviewStatus,
    riskRatings,
  ]);

  const actions: MdaFilterActions = {
    onClientChange: setClient,
    onRiskChange: (_event, value) => {
      setRiskRatings(value);
    },
    onReviewStatusChange: (_event, value) => {
      if (value) setReviewStatus(value);
    },
    onClaimStatusChange: setClaimStatuses,
    onIncidentDateFromChange: setIncidentDateFrom,
    onIncidentDateToChange: setIncidentDateTo,
    onApply: async () => {
      if (!selectedClientId || !canApplyFilters) return;

      setClaimsLoading(true);
      try {
        const response = await getMdaClaims(selectedClientId, {
          filters: buildAppliedFilters(
            filterOptions,
            riskRatings,
            reviewStatus,
            claimStatuses,
            incidentDateFrom,
            incidentDateTo,
          ),
        });
        setClaims(response.claims);
        setHasApplied(true);
      } catch {
        setClaims([]);
        setHasApplied(true);
      } finally {
        setClaimsLoading(false);
      }
    },
    onClear: () => {
      setClient("");
      setRiskRatings([]);
      setReviewStatus("");
      setClaimStatuses([]);
      setIncidentDateFrom(null);
      setIncidentDateTo(null);
      setClaims([]);
      setHasApplied(false);
    },
    onRemove: (key) => {
      if (key === "client") setClient("");
      if (key.startsWith("risk-")) {
        setRiskRatings((current) =>
          current.filter((rating) => `risk-${rating}` !== key),
        );
      }
      if (key === "read" || key === "unread") setReviewStatus("");
      if (key === "claim-status-all") {
        setClaimStatuses([]);
      } else if (key.startsWith("claim-status-")) {
        const statusToRemove = key.replace("claim-status-", "");
        setClaimStatuses((current) =>
          current.filter((status) => status !== statusToRemove),
        );
      }
      if (key === "incident-date") {
        setIncidentDateFrom(null);
        setIncidentDateTo(null);
      }
    },
  };

  const handleChooseMode = (nextMode: Exclude<MdaSearchMode, "choose">) => {
    setClaims([]);
    setHasApplied(false);
    setMode(nextMode);
  };

  const handleBack = () => {
    // Leaving a flow discards its filters and results so the next one starts clean.
    actions.onClear();
    setMode("choose");
  };

  const handleReferenceSearch = async (claimReference: string) => {
    setClaimsLoading(true);
    try {
      const response = await searchMdaClaimsByReference(claimReference);
      setClaims(response.claims);
    } catch {
      setClaims([]);
      // Unlike an empty result, a failure must not read as "no such claim".
      showNotification({
        message: "Unable to search for that claim reference. Please try again.",
        severity: "error",
        duration: 3000,
      });
    } finally {
      setHasApplied(true);
      setClaimsLoading(false);
    }
  };

  const handleOnRequestSpreadsheetClick = async () => {
    if (!selectedClaims.length) {
      setShowRequestSpreadsheetDialog(false);
      return;
    }
    setShowRequestSpreadsheetDialog(true);
  };

  const handleMarkSelectedClaimsAsRead = async () => {
    if (!selectedClaims.length) return;

    setMarkAsReadLoading(true);
    try {
      await markMdaClaimsAsRead(selectedClaims);
      setShowRequestSpreadsheetDialog(false);
      showNotification({
        message:
          "Spreadsheet request sent. An email will be sent to the associated email address.",
        severity: "success",
        duration: 3000,
      });
      setClient("");
      setFilterOptions([]);
      setRiskRatings([]);
      setReviewStatus("");
      setClaimStatuses([]);
      setIncidentDateFrom(null);
      setIncidentDateTo(null);
      setClaims([]);
      setHasApplied(false);
    } catch {
      setShowRequestSpreadsheetDialog(false);
      showNotification({
        message: "Unable to request the spreadsheet. Please try again.",
        severity: "error",
        duration: 3000,
      });
    } finally {
      setMarkAsReadLoading(false);
      setShowRequestSpreadsheetDialog(false);
    }
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: "calc(100dvh - 150px)",
        backgroundColor: "white",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 3,
        boxShadow: "0 2px 10px rgba(15, 35, 65, 0.06)",
        display: "flex",
        flexDirection: "column",
        // Scroll rather than clip when the header and filters leave too little
        // height for the table (e.g. on a laptop screen).
        overflowY: "auto",
        px: { xs: 1.5, sm: 3, lg: 4 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <MdaPageHeader
        markAsReadDisabled={!selectedClaims.length}
        markAsReadLoading={markAsReadLoading}
        onMarkAsRead={handleOnRequestSpreadsheetClick}
        showRequestSpreadsheet={mode !== "choose"}
      />
      {mode === "choose" ? (
        <MdaModeChooser onChoose={handleChooseMode} />
      ) : (
        <Stack
          spacing={{ xs: 2, sm: 3 }}
          sx={{
            flex: "1 0 auto",
            mx: "auto",
            width: "100%",
          }}
        >
          <Box>
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{ color: "#1769ff", fontWeight: 600, textTransform: "none" }}
            >
              Back
            </Button>
          </Box>
          {mode === "reference" ? (
            <MdaClaimReferenceSearch
              loading={claimsLoading}
              onSearch={handleReferenceSearch}
            />
          ) : (
            <MdaFiltersPanel
              filterOptions={renderedFilters}
              onFilterChange={handleFilterChange}
              values={values}
              clients={clientOptions.map(({ id, name }) => ({ id, name }))}
              claimStatusOptions={claimStatusOptions}
              claimStatusesLoading={filterOptionsLoading}
              riskRatingOptions={riskRatingOptions}
              reviewStatusOptions={reviewStatusOptions}
              activeFilters={activeFilters}
              canApplyFilters={canApplyFilters}
              actions={actions}
            />
          )}
          {claims.length || claimsLoading ? (
            <MdaClaimsTable
              claims={claims}
              loading={claimsLoading}
              fillHeight
              onClaimsChange={setClaims}
              onViewRuleBreaks={handleViewRuleBreaks}
            />
          ) : (
            <MdaEmptyState
              hasApplied={hasApplied}
              idleMessage={
                mode === "reference"
                  ? "Enter a claim reference above to find a claim."
                  : undefined
              }
              noResultsMessage={
                mode === "reference"
                  ? "No claim matches that reference."
                  : undefined
              }
            />
          )}
        </Stack>
      )}
      <RequestSpreadsheetDialog
        claims={showRequestSpreadsheetDialog ? selectedClaims : null}
        isCompleting={markAsReadLoading}
        onClose={() => setShowRequestSpreadsheetDialog(false)}
        onComplete={handleMarkSelectedClaimsAsRead}
      />
      <MdaClaimRuleBreaksDrawer
        claim={ruleBreaksClaim}
        onClose={() => setRuleBreaksNavigation(null)}
        position={
          ruleBreaksNavigation
            ? {
                index: ruleBreaksNavigation.index,
                total: ruleBreaksNavigation.claims.length,
              }
            : undefined
        }
        onPrevious={
          ruleBreaksNavigation && ruleBreaksNavigation.index > 0
            ? () => stepRuleBreaks(-1)
            : undefined
        }
        onNext={
          ruleBreaksNavigation &&
          ruleBreaksNavigation.index < ruleBreaksNavigation.claims.length - 1
            ? () => stepRuleBreaks(1)
            : undefined
        }
      />
    </Box>
  );
};

export default Mda;
